<template>
  <div>
    <div class="text-caption font-weight-bold grey--text mb-2">CHỌN VÙNG TÌM KIẾM</div>
    
    <div class="d-flex justify-space-between mb-4">
      <v-btn small outlined color="primary" @click="startDraw('drag_circle')" :class="{ 'v-btn--active': activeMode === 'drag_circle' }">
        <v-icon small left>mdi-record-circle-outline</v-icon> Bán kính
      </v-btn>
      <v-btn small outlined color="primary" @click="startDraw('draw_polygon')" :class="{ 'v-btn--active': activeMode === 'draw_polygon' }">
        <v-icon small left>mdi-shape-polygon-plus</v-icon> Đa giác
      </v-btn>
      <v-btn small outlined color="primary" @click="startDraw('draw_rectangle')" :class="{ 'v-btn--active': activeMode === 'draw_rectangle' }">
        <v-icon small left>mdi-rectangle-outline</v-icon> Chữ nhật
      </v-btn>
    </div>

    <div v-if="activeMode" class="text-caption grey--text mb-4 px-2 py-2 rounded" style="background-color: #f5f5f5; line-height: 1.4;">
      <v-icon small color="info" left>mdi-information</v-icon>
      <span v-if="activeMode === 'drag_circle'"><b>Bán kính:</b> Nhấp chuột tại tâm và <b>Kéo</b> để mở rộng bán kính, sau đó nhả chuột để kết thúc.</span>
      <span v-if="activeMode === 'draw_polygon'"><b>Đa giác:</b> Nhấp chuột để vẽ các góc, <b>nhấp đúp chuột</b> (hoặc Enter) để kết thúc.</span>
      <span v-if="activeMode === 'draw_rectangle'"><b>Chữ nhật:</b> Nhấp chuột vào 1 góc, di chuyển và nhấp lần nữa vào góc đối diện.</span>
      <div class="mt-1 font-italic" style="color: #666; font-size: 0.9em;">(Nhấn lại nút đang chọn hoặc phím ESC để hủy)</div>
    </div>

    <div v-if="spatialData" class="pa-2 bg-light-blue rounded d-flex align-center">
      <v-icon color="success" left>mdi-check-circle</v-icon>
      <div class="d-flex flex-column ml-2">
        <span class="text-caption font-weight-bold">
          Đã vẽ {{ spatialData.type === 'radius' ? 'Bán kính (' + Math.round(spatialData.radius) + 'm)' : spatialData.type === 'polygon' ? 'Đa giác' : 'Chữ nhật' }}
        </span>
        <a @click.prevent="previewSpatial" class="text-caption primary--text" style="text-decoration: underline; cursor: pointer;">
          {{ isPreviewing ? 'Ẩn khỏi bản đồ' : 'Xem lại trên bản đồ' }}
        </a>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon small color="error" @click="clearDraw">
        <v-icon small>mdi-delete</v-icon>
      </v-btn>
    </div>
    <div v-else class="text-caption grey--text font-italic text-center py-2">
      Chưa chọn vùng không gian nào
    </div>
  </div>
</template>

<script>
import { draw, activeDraw, clearDraw as clearMapDraw } from '@components/Map/store/store-draw'
import center from '@turf/center'
import bbox from '@turf/bbox'
import { circle } from '@turf/turf'

export default {
  name: 'SpatialFilterTools',
  props: {
    value: {
      type: Object,
      default: null,
    },
    feature: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    activeMode: null,
    spatialData: null,
    originalFeature: null,
    isPreviewing: false
  }),
  created() {
    if (this.value) this.spatialData = this.value
    if (this.feature) this.originalFeature = this.feature
  },
  methods: {
    startDraw(mode) {
      if (this.activeMode === mode) {
        this.cancelActiveDraw()
        return
      }
      
      this.activeMode = mode
      this.spatialData = null
      this.originalFeature = null
      this.isPreviewing = false
      this.$emit('update:spatial', null)
      this.$emit('update:feature', null)
      clearMapDraw()
      
      try {
        activeDraw()
        draw(mode, (res) => {
          this.activeMode = null
          const features = res?.geojson?.features || []
          if (features.length > 0) {
            this.processDrawResult(features[features.length - 1], mode)
          }
        })
      } catch (e) {
        console.warn('Draw error', e)
        this.activeMode = null
      }
    },
    processDrawResult(feature, mode) {
      let filter = null
      this.originalFeature = JSON.parse(JSON.stringify(feature))
      
      if (mode === 'drag_circle') {
        const centerPt = center(feature)
        const radius = (feature.properties.radiusInKm || 0) * 1000 // mét
        filter = {
          type: 'radius',
          point: centerPt.geometry.coordinates,
          radius: radius
        }
      } else if (mode === 'draw_polygon') {
        filter = {
          type: 'polygon',
          geojson: feature.geometry
        }
      } else if (mode === 'draw_rectangle') {
        const bounds = bbox(feature) // [minX, minY, maxX, maxY]
        filter = {
          type: 'bbox',
          bbox: bounds
        }
      }
      
      this.spatialData = filter
      this.$emit('update:spatial', filter)
      this.$emit('update:feature', this.originalFeature)
      this.$nextTick(() => this.showFeatureOnMap(this.originalFeature))
    },
    clearDraw() {
      this.spatialData = null
      this.activeMode = null
      this.originalFeature = null
      this.isPreviewing = false
      this.$emit('update:spatial', null)
      this.$emit('update:feature', null)
      clearMapDraw()
    },
    previewSpatial() {
      if (this.isPreviewing) {
        try {
          clearMapDraw()
        } catch (e) {
          console.warn('Preview error', e)
        }
        this.isPreviewing = false
        return
      }
      this.restoreOnMap()
    },
    restoreOnMap() {
      const feature =
        this.originalFeature || this.feature || this.featureFromFilter(this.value || this.spatialData)
      if (!feature) return
      this.originalFeature = feature
      if (!this.spatialData && (this.value || feature)) {
        this.spatialData = this.value || this.spatialData
      }
      this.showFeatureOnMap(feature)
    },
    showFeatureOnMap(feature, attempt = 0) {
      if (!feature) return
      try {
        clearMapDraw()
        activeDraw(feature)
        this.isPreviewing = true
        this.$emit('fit-feature', feature)
      } catch (e) {
        if (attempt < 12) {
          setTimeout(() => this.showFeatureOnMap(feature, attempt + 1), 150)
        } else {
          console.warn('Preview error', e)
        }
      }
    },
    featureFromFilter(filter) {
      if (!filter || !filter.type) return null
      if (filter.type === 'polygon' && filter.geojson) {
        return {
          type: 'Feature',
          properties: {},
          geometry: filter.geojson,
        }
      }
      if (filter.type === 'bbox' && Array.isArray(filter.bbox) && filter.bbox.length >= 4) {
        const [minX, minY, maxX, maxY] = filter.bbox.map(Number)
        if (![minX, minY, maxX, maxY].every((value) => Number.isFinite(value))) {
          return null
        }
        return {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [minX, minY],
                [maxX, minY],
                [maxX, maxY],
                [minX, maxY],
                [minX, minY],
              ],
            ],
          },
        }
      }
      if (
        filter.type === 'radius' &&
        Array.isArray(filter.point) &&
        filter.point.length >= 2 &&
        Number.isFinite(Number(filter.radius))
      ) {
        return circle(filter.point, Number(filter.radius) / 1000, {
          steps: 64,
          units: 'kilometers',
        })
      }
      return null
    },
    cancelActiveDraw() {
      this.activeMode = null
      clearMapDraw()
    }
  }
}
</script>

<style scoped>
.bg-light-blue {
  background-color: #e3f2fd;
}
</style>
